import { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '' });
      
      setTimeout(() => {
        setSubmitStatus('idle');
      }, 3000);
    }, 1500);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <section id="contact" className="py-24 bg-gray-900 relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-yellow-500 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-600 rounded-full filter blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Let's Build Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600">Dream Home</span>
          </h2>
          <p className="text-lg text-gray-300 max-w-2xl mx-auto">
            Ready to start your luxury construction project? Get in touch with our team today
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="space-y-8">
            <div className="flex items-start space-x-4">
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg flex-shrink-0">
                <i className="ri-map-pin-line text-2xl text-black"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Address</h3>
                <a 
                  href="https://www.google.com/maps/search/?api=1&query=112+W+Pipeline+Rd+Suite+6+Hurst+TX+76053" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-yellow-400 transition-colors text-lg cursor-pointer"
                >
                  112 W Pipeline Rd Suite 6<br />
                  Hurst, TX 76053
                </a>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg flex-shrink-0">
                <i className="ri-phone-line text-2xl text-black"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Phone</h3>
                <a href="tel:214-864-0689" className="text-gray-300 hover:text-yellow-400 transition-colors text-lg">
                  214-864-0689
                </a>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg flex-shrink-0">
                <i className="ri-mail-line text-2xl text-black"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Email</h3>
                <a href="mailto:richluxuryhb01@gmail.com" className="text-gray-300 hover:text-yellow-400 transition-colors text-lg break-all">
                  richluxuryhb01@gmail.com
                </a>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="w-14 h-14 flex items-center justify-center bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg flex-shrink-0">
                <i className="ri-time-line text-2xl text-black"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Business Hours</h3>
                <p className="text-gray-300 text-lg">Monday - Friday: 8:00 AM - 6:00 PM</p>
                <p className="text-gray-300 text-lg">Saturday: 9:00 AM - 4:00 PM</p>
                <p className="text-gray-300 text-lg">Sunday: Closed</p>
              </div>
            </div>

            <div className="mt-8">
              <div className="w-full h-64 rounded-lg overflow-hidden shadow-2xl">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3351.8!2d-97.1806!3d32.8234!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzLCsDQ5JzI0LjIiTiA5N8KwMTAnNTAuMiJX!5e0!3m2!1sen!2sus!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Rich Luxury Home Builder LLC Location"
                ></iframe>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-8 shadow-2xl">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                  Phone Number
                </label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm"
                  placeholder="(214) 864-0689"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
                  Project Details
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-sm resize-none"
                  placeholder="Tell us about your dream project..."
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-6 py-4 rounded-md text-base font-bold hover:from-yellow-600 hover:to-yellow-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-md text-sm">
                  Thank you! We'll get back to you soon.
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
